import React from 'react';

const WIDTH = 40;
const HEIGHT = 80;
const TUBE_WIDTH = 12;
const BULB_RADIUS = 10;
const TUBE_X = WIDTH / 2;
const TUBE_TOP = 8;
const TUBE_BOTTOM = HEIGHT - BULB_RADIUS - 6;
const TUBE_HEIGHT = TUBE_BOTTOM - TUBE_TOP;

/**
 * Classic vertical thermometer SVG widget for temperature.
 *
 * @param {{ value: number, unit: string, min: number, max: number }} props
 */
export default function ThermometerWidget({
  value = 20,
  unit = '\u00b0C',
  min = -10,
  max = 50,
}) {
  const clamped = Math.max(min, Math.min(max, value));
  const ratio = (clamped - min) / (max - min);
  const mercuryHeight = Math.max(2, TUBE_HEIGHT * ratio);
  const mercuryY = TUBE_BOTTOM - mercuryHeight;

  // Color shifts from blue (cold) through normal to red (hot)
  const mercuryColor =
    clamped <= 0
      ? '#60A5FA'
      : clamped <= 18
        ? '#3B82F6'
        : clamped <= 30
          ? '#F59E0B'
          : '#EF4444';

  const displayValue =
    Number.isFinite(value) ? (Number.isInteger(value) ? value : value.toFixed(1)) : '--';

  return (
    <svg
      width={WIDTH * 2}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH * 2} ${HEIGHT}`}
      className="block"
      role="img"
      aria-label={`${displayValue} ${unit}`}
    >
      {/* Tube background */}
      <rect
        x={TUBE_X - TUBE_WIDTH / 2}
        y={TUBE_TOP}
        width={TUBE_WIDTH}
        height={TUBE_HEIGHT}
        rx={TUBE_WIDTH / 2}
        fill="#E5E7EB"
      />

      {/* Bulb background */}
      <circle
        cx={TUBE_X}
        cy={TUBE_BOTTOM + 4}
        r={BULB_RADIUS}
        fill="#E5E7EB"
      />

      {/* Mercury column */}
      <rect
        x={TUBE_X - TUBE_WIDTH / 2 + 2}
        y={mercuryY}
        width={TUBE_WIDTH - 4}
        height={TUBE_BOTTOM - mercuryY + 4}
        rx={3}
        fill={mercuryColor}
        style={{ transition: 'y 0.6s ease, height 0.6s ease, fill 0.4s ease' }}
      />

      {/* Mercury bulb */}
      <circle
        cx={TUBE_X}
        cy={TUBE_BOTTOM + 4}
        r={BULB_RADIUS - 2}
        fill={mercuryColor}
        style={{ transition: 'fill 0.4s ease' }}
      />

      {/* Tick marks */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = TUBE_BOTTOM - TUBE_HEIGHT * t;
        return (
          <line
            key={t}
            x1={TUBE_X + TUBE_WIDTH / 2 + 2}
            y1={y}
            x2={TUBE_X + TUBE_WIDTH / 2 + 6}
            y2={y}
            stroke="#9CA3AF"
            strokeWidth={1}
          />
        );
      })}

      {/* Value + unit text on the right */}
      <text
        x={TUBE_X + TUBE_WIDTH / 2 + 10}
        y={HEIGHT / 2 - 4}
        dominantBaseline="central"
        className="font-semibold"
        style={{ fontSize: 14, fill: '#1F2937' }}
      >
        {displayValue}
      </text>
      <text
        x={TUBE_X + TUBE_WIDTH / 2 + 10}
        y={HEIGHT / 2 + 12}
        dominantBaseline="central"
        style={{ fontSize: 10, fill: '#9CA3AF' }}
      >
        {unit}
      </text>
    </svg>
  );
}
